import type { NestedFilter } from '@@partner:types:graphql'
import type { Promocode } from '@@partner:types:app'

import React, { useCallback, useMemo, useState } from 'react'
import { generatePath, Link, useParams } from 'react-router-dom'

import { useFetchAllPromocodes } from '@@partner:graphql/query/promocode/allPromocodes.gql'

import { Button, FlexboxGrid, Pagination, SelectPicker, Tag } from 'rsuite'

import PromocodesFilter from '@components/marketing/promocode/grid/PromocodesFilter'
import GraphQLCommonErrorDisplay from '@components/common/GraphQLCommonErrorDisplay'
import ResponsiveTable, { ResponsiveTableColumn } from '@components/common/ResponsiveTable/ResponsiveTable'

import { useGridHelper } from '@@partner:helpers/crud/grid'
import { formatDate } from '@@partner:helpers/util'
import { preparePromocodeFilter } from '@@partner:helpers/promocode'

import { ROUTE_MARKETING_PROMOCODE_EDIT } from '@@partner:config/routes'

const defaultPromocodeFilter: NestedFilter = preparePromocodeFilter({})

export default function PromocodesPage () {
  const { brandSlug } = useParams<any>()

  const [ filter, setFilter ] = useState<NestedFilter>(defaultPromocodeFilter)

  const {
    page,
    limit,
    defaultLimitOptions,
    tableSort,
    handlePageSelect,
    handleLimitSelect,
    handleSortColumn
  } = useGridHelper()

  const { data, loading, error } = useFetchAllPromocodes({
    filter,
    pager: { page, limit },
    sort: {
      fieldName: tableSort.fieldName,
      direction: tableSort.direction
    }
  })

  const handleFilterChanged = useCallback((updatedFilter: NestedFilter) => {
    setFilter(updatedFilter)
    handlePageSelect(1)
  }, [ handlePageSelect ])

  const promocodes = data?.promocodes?.edges ?? []
  const pageInfo = data?.promocodes?.pageInfo

  // Колонки для ResponsiveTable
  const columns: ResponsiveTableColumn<Promocode>[] = useMemo(() => [
    {
      dataKey: 'id',
      header: 'ИД',
      sortable: true,
      flexGrow: 1,
      isCardTitle: true
    },
    {
      dataKey: 'code',
      header: 'Промокод',
      sortable: true,
      flexGrow: 2,
      render: (row) => <Tag color="blue">{row.code}</Tag>
    },
    {
      dataKey: 'status',
      header: 'Статус',
      sortable: true,
      flexGrow: 1,
      isCardBadge: true,
      render: (row) => row.is_active
        ? <Tag color="green">Включен</Tag>
        : <Tag color="red">Выключен</Tag>
    },
    {
      dataKey: 'discounts',
      header: 'Скидка (корзина/доставка/макс.)',
      flexGrow: 3,
      hideOnMobile: true,
      render: (row) => (
        <>
          <Tag>
            {row.cart_total_discount}
            {row.discount_in_percents ? '%' : ''}
          </Tag>
          <Tag>
            {row.delivery_discount}
            {row.delivery_discount_in_percent ? '%' : ''}
          </Tag>
          <Tag>
            {row.max_order_discount}
            {row.max_order_discount_in_percent ? '%' : ''}
          </Tag>
        </>
      )
    },
    {
      dataKey: 'activated_count',
      header: 'Активаций',
      sortable: true,
      flexGrow: 1,
      render: (row) => <Tag color="orange">{row.activated_count}</Tag>
    },
    {
      dataKey: 'limits',
      header: 'Лимит (на юзера/всего)',
      flexGrow: 2,
      hideOnMobile: true,
      render: (row) => (
        <>
          <Tag>
            {row.max_customer_activation_count === -1 ? '∞' : row.max_customer_activation_count}
          </Tag>
          <span>/</span>
          <Tag>
            {row.max_total_activation_count === -1 ? '∞' : row.max_total_activation_count}
          </Tag>
        </>
      )
    },
    {
      dataKey: 'active_dates',
      header: 'Срок действия',
      flexGrow: 2,
      hideOnMobile: true,
      render: (row) => (
        <>
          {row.active_from && (
            <Tag>{formatDate(new Date(row.active_from), false)}</Tag>
          )}
          {row.active_until && (
            <>
              <span> / </span>
              <Tag>{formatDate(new Date(row.active_until), false)}</Tag>
            </>
          )}
        </>
      )
    }
  ], [])

  const renderActions = useCallback((promocode: Promocode) => (
    <Button
      appearance="primary"
      color="cyan"
      as={Link}
      to={generatePath(ROUTE_MARKETING_PROMOCODE_EDIT, {
        brandSlug,
        promocodeId: promocode.id
      })}
      size="xs"
    >
      Изменить
    </Button>
  ), [ brandSlug ])

  if (error) {
    return (
      <GraphQLCommonErrorDisplay
        title="Ошибка загрузки промокодов"
        error={error}
      />
    )
  }

  return (
    <div className="PromocodesPage">
      <PromocodesFilter onFilterChanged={handleFilterChanged} />

      <ResponsiveTable<Promocode>
        data={promocodes}
        columns={columns}
        loading={loading}
        rowKey="id"
        sortColumn={tableSort.fieldName}
        sortType={tableSort.sortType}
        onSortColumn={handleSortColumn}
        renderActions={renderActions}
        emptyMessage="Нет промокодов"
      />

      <FlexboxGrid
        justify="space-between"
        align="middle"
        style={{ marginTop: 10 }}
      >
        <Pagination
          last
          first
          next
          prev
          ellipsis
          activePage={page}
          pages={pageInfo?.lastPage}
          total={pageInfo?.total ?? 0}
          maxButtons={10}
          onSelect={handlePageSelect}
        />
        <SelectPicker
          value={limit}
          data={defaultLimitOptions.map((key) => ({ value: key, label: key }))}
          cleanable={false}
          searchable={false}
          onChange={handleLimitSelect}
        />
      </FlexboxGrid>
    </div>
  )
}
